<template>
  <div id="mutasi-produsen-kegiatan" class="content content--borderless px-2 px-md-4 px-lg-5">
    <!-- Breadcrumbs -->
    <div class="content__breadcrumbs">
      <Breadcrumbs :items="breadcrumbs" />
    </div>

    <!-- Header -->
    <div class="content__header mb-4">
      <div class="content__main-header align-items-center">
        <div class="content__title">
          <div class="content__title__name d-flex align-items-center">
            <VueFeather type="repeat" size="22" class="mr-2 text-primary" style="display:block;"></VueFeather>
            Mutasi Produsen Data Kegiatan
          </div>
          <div class="content__title__description">
            Pemindahan kepemilikan instansi produsen data pada Metadata Kegiatan beserta seluruh MS-Variabel dan MS-Indikator terkait.
          </div>
        </div>

        <div class="ml-auto d-flex align-items-center" v-if="canAccess">
          <span class="badge badge-light border text-primary px-3 py-2 text-12 font-weight-bold">
            <VueFeather type="shield" size="13" class="mr-1" style="display:inline-block; vertical-align: middle;"></VueFeather>
            {{ accessBadgeText }}
          </span>
        </div>
      </div>
    </div>

    <!-- Alert Guard jika tidak punya akses -->
    <div v-if="!canAccess" class="alert alert-danger d-flex align-items-center p-3">
      <VueFeather type="alert-octagon" size="24" class="mr-3 text-danger"></VueFeather>
      <div>
        <div class="font-weight-bold">Akses Ditolak</div>
        <div class="text-13">Halaman ini hanya dapat diakses oleh <strong>Superadmin</strong> atau <strong>Pembina Data Pusat (Dit. Diseminasi Statistik BPS RI)</strong>.</div>
      </div>
    </div>

    <!-- Konten Utama Form -->
    <div v-else class="content__body">
      <!-- INFO BANNER -->
      <div class="alert alert-info border-info d-flex align-items-start p-3 mb-4" style="background-color: #f0f9ff; border-left: 4px solid #0284c7;">
        <VueFeather type="info" size="20" class="mr-2 text-info flex-shrink-0 mt-1"></VueFeather>
        <div class="text-13 text-secondary">
          <strong>Perhatian:</strong> Perubahan instansi produsen data akan memperbarui atribut <code>produsen_data_id</code>, <code>produsen_data_name</code>, serta kode provinsi dan kabupaten/kota pada kegiatan terpilih dan seluruh variabel/indikator anak di dalamnya secara berjenjang (*cascade*).
        </div>
      </div>

      <!-- STEP 1: PENCARIAN & IDENTIFIKASI KEGIATAN -->
      <div class="card border rounded-lg shadow-sm mb-4">
        <div class="card-header bg-white py-3 px-4 px-md-5 border-bottom d-flex align-items-center">
          <span class="badge badge-primary rounded-circle mr-2 px-2 py-1 text-12">1</span>
          <h6 class="mb-0 font-weight-bold text-dark">Pilih Kegiatan yang Akan Dimutasi</h6>
        </div>
        <div class="card-body p-4 px-md-5">
          <div class="row align-items-end">
            <div class="col-md-5">
              <label class="font-weight-bold text-13 mb-1">ID Kegiatan MMS / Indah:</label>
              <div class="input-group">
                <input 
                  type="number" 
                  class="form-control" 
                  placeholder="Contoh: 84732" 
                  v-model="searchId"
                  @keyup.enter="searchKegiatan"
                />
                <div class="input-group-append">
                  <button 
                    class="btn btn-primary px-3 d-flex align-items-center" 
                    type="button" 
                    @click="searchKegiatan"
                    :disabled="isLoadingKegiatan || !searchId"
                  >
                    <b-spinner small class="mr-1" v-if="isLoadingKegiatan"></b-spinner>
                    <VueFeather type="search" size="14" class="mr-1" v-else></VueFeather>
                    Cari Kegiatan
                  </button>
                </div>
              </div>
            </div>
            <div class="col-md-7 mt-2 mt-md-0">
              <span class="text-12 text-muted">Contoh pengujian: </span>
              <button type="button" class="btn btn-sm btn-outline-secondary py-0 px-2 text-12 mr-1" @click="searchId = '84732'; searchKegiatan()">ID: 84732</button>
              <button type="button" class="btn btn-sm btn-outline-secondary py-0 px-2 text-12" @click="searchId = '106359'; searchKegiatan()">ID: 106359</button>
            </div>
          </div>

          <!-- Alert Error Not Found -->
          <div v-if="searchError" class="alert alert-warning mt-3 mb-0 text-13">
            <VueFeather type="alert-circle" size="14" class="mr-1"></VueFeather>
            {{ searchError }}
          </div>

          <!-- HASIL KEGIATAN TERPILIH -->
          <div v-if="selectedKegiatan" class="mt-4 pt-3 border-top">
            <div class="row">
              <div class="col-lg-7">
                <div class="p-3 rounded bg-light border">
                  <div class="d-flex align-items-center mb-2">
                    <span class="badge badge-secondary mr-2">ID: {{ selectedKegiatan.id }}</span>
                    <span :class="'status status--' + (selectedKegiatan.status ? selectedKegiatan.status.toLowerCase() : 'draft')">
                      {{ selectedKegiatan.status }}
                    </span>
                    <span class="badge badge-info ml-2" v-if="selectedKegiatan.submission_period || selectedKegiatan.year">
                      Tahun {{ selectedKegiatan.submission_period || selectedKegiatan.year }}
                    </span>
                  </div>
                  <h5 class="font-weight-bold text-dark mb-2">
                    {{ getKegiatanTitle(selectedKegiatan) }}
                  </h5>
                  <div class="text-13 text-muted mb-2">
                    <VueFeather type="map-pin" size="13" class="mr-1 text-primary"></VueFeather>
                    Cara Pengumpulan: <strong>{{ getKegiatanCara(selectedKegiatan) }}</strong>
                  </div>

                  <div class="p-2 rounded bg-white border border-danger-light mt-3">
                    <div class="text-11 font-weight-bold text-uppercase text-danger mb-1">
                      <VueFeather type="home" size="12" class="mr-1"></VueFeather>
                      Produsen Data Saat Ini (Asal):
                    </div>
                    <div class="font-weight-bold text-dark text-14">
                      {{ getProdusenAsal(selectedKegiatan) }}
                    </div>
                    <div class="text-12 text-muted mt-1" v-if="selectedKegiatan.produsen_data_id">
                      ID Organisasi: <code>{{ selectedKegiatan.produsen_data_id }}</code> 
                      <span class="ml-2" v-if="selectedKegiatan.produsen_data_province_code">
                        • Prov: {{ selectedKegiatan.produsen_data_province_code }}, Kab: {{ selectedKegiatan.produsen_data_city_code || '00' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Metrik Anak (Variabel & Indikator) -->
              <div class="col-lg-5 mt-3 mt-lg-0">
                <div class="row h-100">
                  <div class="col-6">
                    <div class="card h-100 border text-center p-3 bg-light">
                      <div class="text-muted text-12 font-weight-bold text-uppercase mb-1">MS-Variabel</div>
                      <div class="display-4 font-weight-bold text-primary mb-1">
                        {{ childVariables.length }}
                      </div>
                      <div class="text-11 text-muted">Variabel Terhubung</div>
                    </div>
                  </div>
                  <div class="col-6">
                    <div class="card h-100 border text-center p-3 bg-light">
                      <div class="text-muted text-12 font-weight-bold text-uppercase mb-1">MS-Indikator</div>
                      <div class="display-4 font-weight-bold text-info mb-1">
                        {{ childIndicators.length }}
                      </div>
                      <div class="text-11 text-muted">Indikator Terhubung</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Preview Toggle List Anak -->
            <div class="mt-3">
              <button 
                class="btn btn-link btn-sm p-0 text-12 text-primary font-weight-bold" 
                type="button" 
                @click="showChildDetails = !showChildDetails"
              >
                <VueFeather :type="showChildDetails ? 'chevron-up' : 'chevron-down'" size="13" class="mr-1"></VueFeather>
                {{ showChildDetails ? 'Sembunyikan' : 'Tampilkan' }} Rincian Variabel & Indikator Anak
              </button>

              <b-collapse v-model="showChildDetails" class="mt-2">
                <div class="row">
                  <div class="col-md-6 mb-2">
                    <div class="p-2 border rounded bg-white text-12" style="max-height: 200px; overflow-y: auto;">
                      <div class="font-weight-bold text-dark border-bottom pb-1 mb-2">Daftar Variabel ({{ childVariables.length }})</div>
                      <div v-for="(v, vi) in childVariables" :key="'var-'+vi" class="py-1 border-bottom text-truncate">
                        {{ vi+1 }}. {{ (v && v.data && v.data.nama) ? v.data.nama : (v ? v.name : '') }}
                      </div>
                      <div v-if="childVariables.length === 0" class="text-muted italic">Tidak ada variabel.</div>
                    </div>
                  </div>
                  <div class="col-md-6 mb-2">
                    <div class="p-2 border rounded bg-white text-12" style="max-height: 200px; overflow-y: auto;">
                      <div class="font-weight-bold text-dark border-bottom pb-1 mb-2">Daftar Indikator ({{ childIndicators.length }})</div>
                      <div v-for="(ind, indi) in childIndicators" :key="'ind-'+indi" class="py-1 border-bottom text-truncate">
                        {{ indi+1 }}. {{ (ind && ind.data && ind.data.nama) ? ind.data.nama : (ind ? ind.name : '') }}
                      </div>
                      <div v-if="childIndicators.length === 0" class="text-muted italic">Tidak ada indikator.</div>
                    </div>
                  </div>
                </div>
              </b-collapse>
            </div>
          </div>
        </div>
      </div>

      <!-- STEP 2: PILIH PRODUSEN DATA TUJUAN -->
      <div v-if="selectedKegiatan" class="card border rounded-lg shadow-sm mb-4">
        <div class="card-header bg-white py-3 px-4 px-md-5 border-bottom d-flex align-items-center">
          <span class="badge badge-primary rounded-circle mr-2 px-2 py-1 text-12">2</span>
          <h6 class="mb-0 font-weight-bold text-dark">Tentukan Produsen Data Baru (Tujuan)</h6>
        </div>
        <div class="card-body p-4 px-md-5">
          <div class="row">
            <div class="col-md-7">
              <div class="form-group mb-3">
                <label class="font-weight-bold text-13">Pilih Instansi Produsen Data SDI Baru: <span class="text-danger">*</span></label>
                                <Treeselect
                  v-model="targetProdusen"
                  :async="true"
                  :options="produsenOptions"
                  :loadOptions="produsenDataLoadOptions"
                  :normalizer="produsenDataNormalizer"
                  placeholder="Ketik nama Kementerian / Lembaga / Dinas baru . . ."
                  searchPromptText="Ketikkan beberapa huruf terlebih dahulu untuk memulai pencarian..."
                  valueFormat="object"
                  @select="onSelectProdusen"
                  @deselect="onDeselectProdusen"
                  @input="onInputProdusen"
                >
                </Treeselect>
                <small class="form-text text-muted">Contoh: Ketik "Kementerian Pariwisata" untuk memilih instansi pemekaran baru.</small>
              </div>

              <div class="form-group mb-3">
                <label class="font-weight-bold text-13">Unit Kerja Penanggung Jawab Baru (Opsional):</label>
                <input 
                  type="text" 
                  class="form-control" 
                  placeholder="Misal: Direktorat Pemasaran Pariwisata Nusantara"
                  v-model="targetUnitKerja"
                />
              </div>

              <div class="form-group mb-3">
                <label class="font-weight-bold text-13">Dasar / Catatan Mutasi: <span class="text-danger">*</span></label>
                <textarea 
                  class="form-control text-13" 
                  rows="3" 
                  placeholder="Isikan dasar hukum / alasan perubahan (Contoh: Penataan K/L berdasarkan Perpres Pembentukan Kementerian Kabinet Merah Putih)"
                  v-model="mutationReason"
                ></textarea>
              </div>
            </div>

            <!-- Panel Cakupan Cascading -->
            <div class="col-md-5">
              <div class="p-3 bg-light border rounded">
                <div class="font-weight-bold text-dark text-13 mb-2">Cakupan Perubahan Berjenjang (Cascade):</div>
                
                <b-form-checkbox v-model="cascadeOptions.kegiatan" disabled class="mb-2 text-13">
                  <strong>Metadata Kegiatan Utama</strong>
                  <div class="text-muted text-11 pl-4">Wajib dimutasi ke produsen baru</div>
                </b-form-checkbox>

                <b-form-checkbox v-model="cascadeOptions.variabel" class="mb-2 text-13">
                  <strong>Seluruh MS-Variabel ({{ childVariables.length }} item)</strong>
                  <div class="text-muted text-11 pl-4">Perbarui produsen data pada setiap variabel anak</div>
                </b-form-checkbox>

                <b-form-checkbox v-model="cascadeOptions.indikator" class="mb-2 text-13">
                  <strong>Seluruh MS-Indikator ({{ childIndicators.length }} item)</strong>
                  <div class="text-muted text-11 pl-4">Perbarui produsen data pada setiap indikator anak</div>
                </b-form-checkbox>

                <div class="border-top pt-2 mt-2 text-12 text-muted">
                  Total entitas terdampak: <strong>{{ 1 + (cascadeOptions.variabel ? childVariables.length : 0) + (cascadeOptions.indikator ? childIndicators.length : 0) }} data</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- STEP 3: SIDE-BY-SIDE DIFF & EKSEKUSI -->
      <div v-if="selectedKegiatan && selectedTargetOrg" class="card border rounded-lg shadow-sm mb-4">
        <div class="card-header bg-white py-3 px-4 px-md-5 border-bottom d-flex align-items-center">
          <span class="badge badge-primary rounded-circle mr-2 px-2 py-1 text-12">3</span>
          <h6 class="mb-0 font-weight-bold text-dark">Pratinjau Perubahan (Side-by-Side Diff)</h6>
        </div>
        <div class="card-body p-4 px-md-5">
          <div class="row">
            <!-- Kolom Asal -->
            <div class="col-md-6 mb-3 mb-md-0">
              <div class="p-3 border rounded h-100" style="background-color: #fff8f8; border-color: #fca5a5 !important;">
                <div class="text-11 font-weight-bold text-uppercase text-danger mb-2 d-flex align-items-center">
                  <VueFeather type="arrow-left-circle" size="14" class="mr-1"></VueFeather>
                  PRODUSEN ASAL (SEBELUM)
                </div>
                <h5 class="text-dark font-weight-bold mb-1">
                  {{ getProdusenAsal(selectedKegiatan) }}
                </h5>
                <div class="text-12 text-muted mb-2">
                  ID Organisasi: <code>{{ selectedKegiatan.produsen_data_id || '-' }}</code>
                </div>
                <div class="text-12 text-muted">
                  Provinsi: <strong>{{ selectedKegiatan.produsen_data_province_code || '00' }}</strong> • Kab/Kota: <strong>{{ selectedKegiatan.produsen_data_city_code || '00' }}</strong>
                </div>
              </div>
            </div>

            <!-- Kolom Tujuan -->
            <div class="col-md-6">
              <div class="p-3 border rounded h-100" style="background-color: #f0fdf4; border-color: #86efac !important;">
                <div class="text-11 font-weight-bold text-uppercase text-success mb-2 d-flex align-items-center">
                  <VueFeather type="arrow-right-circle" size="14" class="mr-1"></VueFeather>
                  PRODUSEN TUJUAN (SESUDAH)
                </div>
                <h5 class="text-dark font-weight-bold mb-1">
                  {{ selectedTargetOrg.name }}
                </h5>
                <div class="text-12 text-muted mb-2">
                  ID Organisasi: <code>{{ selectedTargetOrg.id }}</code>
                </div>
                <div class="text-12 text-muted">
                  Provinsi: <strong>{{ selectedTargetOrg.provinceCode || '00' }}</strong> • Kab/Kota: <strong>{{ selectedTargetOrg.cityCode || '00' }}</strong>
                </div>
                <div class="text-12 text-muted mt-1" v-if="targetUnitKerja">
                  Unit Kerja: <strong>{{ targetUnitKerja }}</strong>
                </div>
              </div>
            </div>
          </div>

          <!-- Tombol Aksi Mutasi -->
          <div class="d-flex justify-content-end align-items-center border-top pt-3 mt-4">
            <button 
              type="button" 
              class="btn btn-outline-secondary mr-2" 
              @click="resetForm"
            >
              Reset Form
            </button>
            <button 
              type="button" 
              class="btn btn-primary px-4 py-2 font-weight-bold d-flex align-items-center"
              :disabled="!isReadyToMutate"
              @click="showConfirmModal"
            >
              <VueFeather type="check-circle" size="16" class="mr-2"></VueFeather>
              Proses Mutasi Produsen Data
            </button>
          </div>
        </div>
      </div>

    </div>

    <!-- MODAL KONFIRMASI & EKSEKUSI MUTASI -->
    <b-modal
      id="modal-confirm-mutasi"
      ref="mutasiModal"
      size="lg"
      centered
      :no-close-on-backdrop="executionState === 'RUNNING'"
      :no-close-on-esc="executionState === 'RUNNING'"
      hide-header-close
      dialog-class="my-4 px-2 px-md-3"
      header-class="px-4 px-md-5 px-lg-5 pt-4 pb-3 border-bottom-0 bg-white"
      body-class="px-4 px-md-5 px-lg-5 py-3 bg-white"
      footer-class="px-4 px-md-5 px-lg-5 pb-4 pt-3 border-top-0 bg-white"
    >
      <!-- Custom Header -->
      <template #modal-header>
        <div class="d-flex align-items-center w-100">
          <div 
            class="modal-icon-badge mr-3 d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            :class="{
              'bg-primary-light text-primary': executionState === 'IDLE',
              'bg-info-light text-info': executionState === 'RUNNING',
              'bg-success-light text-success': executionState === 'DONE' && progress.failed === 0,
              'bg-warning-light text-warning': executionState === 'DONE' && progress.failed > 0
            }"
            style="width: 46px; height: 46px;"
          >
            <b-spinner small v-if="executionState === 'RUNNING'" variant="primary"></b-spinner>
            <VueFeather v-else-if="executionState === 'DONE'" :type="progress.failed > 0 ? 'alert-triangle' : 'check-circle'" size="22"></VueFeather>
            <VueFeather v-else type="repeat" size="20"></VueFeather>
          </div>
          <div class="flex-grow-1">
            <h5 class="modal-title font-weight-bold mb-1 text-dark" style="font-size: 1.15rem; letter-spacing: -0.2px;">
              {{ executionState === 'IDLE' ? 'Konfirmasi Pemindahan Produsen Data' : (executionState === 'DONE' ? 'Hasil Mutasi Produsen Data' : 'Memproses Pembaruan ke Backend MMS...') }}
            </h5>
            <p class="text-muted small mb-0">
              {{ executionState === 'IDLE' ? 'Periksa kembali parameter mutasi dan cakupan cascading sebelum data disimpan ke database MMS.' : (executionState === 'DONE' ? 'Proses eksekusi mutasi produsen data pada server backend MMS telah selesai.' : 'Menyimpan perubahan metadata kegiatan, variabel, dan indikator terkait.') }}
            </p>
          </div>
        </div>
      </template>

      <!-- MODAL BODY -->
      <div v-if="selectedKegiatan && selectedTargetOrg">
        
        <!-- STATE 1: IDLE / CONFIRMATION REVIEW -->
        <div v-if="executionState === 'IDLE'" class="py-1">
          <!-- Policy Notice -->
          <div class="alert alert-warning border-0 d-flex align-items-start py-3 px-4 mb-3 rounded" style="background-color: #fffbeb; color: #92400e;">
            <VueFeather type="alert-triangle" size="18" class="mr-2 mt-1 flex-shrink-0 text-warning"></VueFeather>
            <div class="small leading-normal">
              Tindakan ini akan memindahkan kepemilikan <strong>Metadata Statistik Kegiatan</strong> beserta seluruh elemen anak yang dipilih ke instansi produsen data baru secara permanen.
            </div>
          </div>

          <!-- Transfer Overview Card (Side-by-Side Bento Grid) -->
          <div class="summary-transfer-card p-4 px-md-4 px-lg-5 rounded border mb-3" style="background-color: #f8fafc;">
            <!-- Baris Informasi Kegiatan Utama -->
            <div class="mb-3 pb-3 border-bottom">
              <span class="text-11 text-uppercase font-weight-bold text-muted d-block mb-1" style="letter-spacing: 0.5px;">Kegiatan Metadata</span>
              <div class="d-flex align-items-center flex-wrap">
                <span class="badge badge-secondary px-2 py-1 mr-2 text-11 font-weight-bold">ID #{{ selectedKegiatan.id }}</span>
                <span class="badge badge-info px-2 py-1 mr-2 text-11 font-weight-bold" v-if="selectedKegiatan.submission_period || selectedKegiatan.year">
                  Tahun {{ selectedKegiatan.submission_period || selectedKegiatan.year }}
                </span>
                <span :class="'status status--' + (selectedKegiatan.status ? selectedKegiatan.status.toLowerCase() : 'draft') + ' mr-2'">
                  {{ selectedKegiatan.status }}
                </span>
                <span class="font-weight-bold text-dark text-14 mt-1 mt-md-0">
                  {{ getKegiatanTitle(selectedKegiatan) }}
                </span>
              </div>
            </div>

            <!-- Transfer Instansi Asal -> Tujuan -->
            <div class="row align-items-center mb-3 pb-3 border-bottom">
              <!-- Asal -->
              <div class="col-md-5">
                <span class="text-11 text-uppercase font-weight-bold text-danger d-block mb-1" style="letter-spacing: 0.5px;">
                  <VueFeather type="home" size="12" class="mr-1"></VueFeather> Produsen Asal (Sebelum)
                </span>
                <div class="font-weight-bold text-dark text-13">
                  {{ getProdusenAsal(selectedKegiatan) }}
                </div>
                <div class="text-11 text-muted mt-1">
                  ID Organisasi: <code>{{ selectedKegiatan.produsen_data_id || '-' }}</code> • Wilayah: Prov {{ selectedKegiatan.produsen_data_province_code || '00' }}
                </div>
              </div>

              <!-- Arrow -->
              <div class="col-md-2 text-center py-2 py-md-0">
                <div class="transfer-arrow-pill d-inline-flex align-items-center justify-content-center rounded-circle bg-white border shadow-xs" style="width: 36px; height: 36px;">
                  <VueFeather type="arrow-right" size="18" class="text-primary"></VueFeather>
                </div>
              </div>

              <!-- Tujuan -->
              <div class="col-md-5">
                <span class="text-11 text-uppercase font-weight-bold text-success d-block mb-1" style="letter-spacing: 0.5px;">
                  <VueFeather type="check-circle" size="12" class="mr-1"></VueFeather> Produsen Tujuan (Sesudah)
                </span>
                <div class="font-weight-bold text-success text-13">
                  {{ selectedTargetOrg.name }}
                </div>
                <div class="text-11 text-muted mt-1">
                  ID Organisasi: <code>{{ selectedTargetOrg.id }}</code> • Wilayah: Prov {{ selectedTargetOrg.provinceCode || '00' }}
                </div>
                <div class="text-11 text-dark mt-1" v-if="targetUnitKerja">
                  Unit Kerja: <strong>{{ targetUnitKerja }}</strong>
                </div>
              </div>
            </div>

            <!-- Consolidated Table Grid: Rincian Atribut & Cakupan Mutasi -->
            <div>
              <span class="text-11 text-uppercase font-weight-bold text-muted d-block mb-2" style="letter-spacing: 0.5px;">
                Tabel Rincian Parameter & Cakupan Mutasi
              </span>
              <div class="table-responsive bg-white rounded border">
                <table class="table table-sm table-striped mb-0 text-12">
                  <tbody>
                    <tr>
                      <td class="font-weight-bold text-muted py-2.5 px-4" style="width: 35%;">Metadata Kegiatan Utama</td>
                      <td class="py-2.5 px-4 text-dark">
                        <span class="badge badge-success px-2 py-1 font-weight-bold mr-1">Ya</span>
                        1 entitas kegiatan (ID: #{{ selectedKegiatan.id }})
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-muted py-2.5 px-4">MS-Variabel Terkait</td>
                      <td class="py-2.5 px-4 text-dark">
                        <span :class="['badge px-2 py-1 font-weight-bold mr-1', cascadeOptions.variabel ? 'badge-success' : 'badge-secondary']">
                          {{ cascadeOptions.variabel ? 'Ya' : 'Tidak' }}
                        </span>
                        <span v-if="cascadeOptions.variabel">{{ childVariables.length }} variabel terhubung akan diperbarui</span>
                        <span v-else class="text-muted">Dilewati / tidak diubah</span>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-muted py-2.5 px-4">MS-Indikator Terkait</td>
                      <td class="py-2.5 px-4 text-dark">
                        <span :class="['badge px-2 py-1 font-weight-bold mr-1', cascadeOptions.indikator ? 'badge-success' : 'badge-secondary']">
                          {{ cascadeOptions.indikator ? 'Ya' : 'Tidak' }}
                        </span>
                        <span v-if="cascadeOptions.indikator">{{ childIndicators.length }} indikator terhubung akan diperbarui</span>
                        <span v-else class="text-muted">Dilewati / tidak diubah</span>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-muted py-2.5 px-4">Dasar / Alasan Mutasi</td>
                      <td class="py-2 px-3 text-dark font-italic">
                        "{{ mutationReason || '-' }}"
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-muted py-2.5 px-4">Petugas Eksekutor</td>
                      <td class="py-2.5 px-4 text-dark">
                        {{ currentUser.name }} — <strong>{{ currentRoleName }}</strong>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-muted py-2.5 px-4">Waktu Perencanaan</td>
                      <td class="py-2.5 px-4 text-dark">
                        {{ executionPlannedTime }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Collapsible Technical API Payload Accordion -->
          <div class="mb-2">
            <button 
              type="button" 
              class="btn btn-sm btn-link p-0 text-12 text-primary font-weight-bold d-inline-flex align-items-center"
              @click="showPayloadPreview = !showPayloadPreview"
            >
              <VueFeather :type="showPayloadPreview ? 'chevron-up' : 'chevron-down'" size="13" class="mr-1"></VueFeather>
              <VueFeather type="code" size="13" class="mr-1"></VueFeather>
              {{ showPayloadPreview ? 'Sembunyikan' : 'Tampilkan' }} Pratinjau Payload Teknis API (JSON)
            </button>
            <b-collapse v-model="showPayloadPreview" class="mt-2">
              <div class="position-relative">
                <button 
                  type="button" 
                  class="btn btn-xs btn-outline-light position-absolute" 
                  style="right: 10px; top: 10px; z-index: 10; font-size: 11px;"
                  @click="copyPayloadJson"
                >
                  <VueFeather type="copy" size="11" class="mr-1"></VueFeather> Salin JSON
                </button>
                <pre class="bg-dark text-light p-3 rounded text-11 mb-0" style="max-height: 180px; overflow-y: auto; font-family: 'Consolas', 'Monaco', monospace; border: 1px solid #334155;">{{ formattedPayloadJson }}</pre>
              </div>
            </b-collapse>
          </div>
        </div>

        <!-- STATE 2 & 3: RUNNING & DONE (DARKROOM TERMINAL CONSOLE) -->
        <div v-else class="py-1">
          <!-- Status Banner -->
          <div class="d-flex justify-content-between align-items-center mb-2">
            <span class="font-weight-bold text-13">
              <span v-if="executionState === 'RUNNING'" class="text-primary d-inline-flex align-items-center">
                <b-spinner small variant="primary" class="mr-2"></b-spinner> Menyimpan perubahan ke backend MMS...
              </span>
              <span v-else-if="progress.failed > 0" class="text-warning d-inline-flex align-items-center">
                <VueFeather type="alert-triangle" size="16" class="mr-2 text-warning"></VueFeather> Mutasi Selesai dengan Sebagian Peringatan
              </span>
              <span v-else class="text-success d-inline-flex align-items-center">
                <VueFeather type="check-circle" size="16" class="mr-2 text-success"></VueFeather> Mutasi Berhasil Dieksekusi!
              </span>
            </span>
            <b-badge :variant="progress.failed > 0 ? 'warning' : (executionState === 'DONE' ? 'success' : 'primary')" class="px-2 py-1 text-12">
              {{ progress.done }} / {{ progress.total }} Item Selesai
            </b-badge>
          </div>

          <!-- Progress Bar -->
          <b-progress
            :value="progress.done"
            :max="progress.total"
            :variant="progress.failed > 0 ? 'warning' : 'primary'"
            striped
            :animated="executionState === 'RUNNING'"
            height="8px"
            class="mb-3 rounded-pill shadow-xs"
          ></b-progress>

          <!-- Darkroom Terminal Console with Mac Dots -->
          <div class="darkroom-console rounded shadow-sm border" style="background-color: #0f172a; border-color: #1e293b !important;">
            <!-- Console Top Bar -->
            <div class="d-flex align-items-center justify-content-between px-4 py-2 border-bottom" style="border-color: #1e293b !important; background-color: #1e293b;">
              <div class="d-flex align-items-center">
                <span class="mac-dot bg-danger mr-1" style="width: 10px; height: 10px; border-radius: 50%; display: inline-block;"></span>
                <span class="mac-dot bg-warning mr-1" style="width: 10px; height: 10px; border-radius: 50%; display: inline-block;"></span>
                <span class="mac-dot bg-success" style="width: 10px; height: 10px; border-radius: 50%; display: inline-block;"></span>
                <span class="text-muted ml-2 text-11" style="font-family: 'Consolas', monospace;">mms-mutation-job.log</span>
              </div>
              <div class="text-11 text-muted">
                {{ executionLogs.length }} event log
              </div>
            </div>

            <!-- Console Log Output -->
            <div 
              ref="consoleLogContainer"
              class="p-4" 
              style="height: 240px; overflow-y: auto; font-family: 'Consolas', 'Monaco', monospace; font-size: 12px; color: #cbd5e1;"
            >
              <div v-for="(log, idx) in executionLogs" :key="idx" class="mb-1 d-flex align-items-start">
                <span class="text-muted mr-2 flex-shrink-0" style="color: #64748b !important;">[{{ log.time }}]</span>
                <span :class="getLogClass(log.type)">{{ log.text }}</span>
              </div>
            </div>
          </div>

          <!-- Result Alert Banner -->
          <div v-if="executionState === 'DONE'" :class="['alert d-flex align-items-center mt-3 mb-0', progress.failed > 0 ? 'alert-warning' : 'alert-success']">
            <VueFeather :type="progress.failed > 0 ? 'alert-triangle' : 'check-circle'" size="20" class="mr-2 flex-shrink-0"></VueFeather>
            <div class="text-13">
              <strong>{{ progress.failed > 0 ? 'Mutasi Selesai dengan Sebagian Peringatan' : 'Mutasi Produsen Berhasil Dieksekusi!' }}</strong><br>
              {{ progress.done }} item berhasil diperbarui di database backend MMS.
            </div>
          </div>
        </div>

      </div>

      <!-- Custom Footer Buttons -->
      <template #modal-footer>
        <div v-if="executionState === 'IDLE'" class="d-flex justify-content-end align-items-center w-100">
          <b-button 
            variant="outline-secondary" 
            class="px-4 py-2 mr-2 font-weight-bold" 
            @click="$refs.mutasiModal.hide()"
          >
            Batal
          </b-button>
          <b-button 
            variant="primary" 
            class="px-4 py-2 font-weight-bold shadow-sm d-inline-flex align-items-center" 
            @click="executeMutasi"
          >
            <VueFeather type="send" size="15" class="mr-2"></VueFeather> 
            Konfirmasi & Eksekusi Mutasi Riil
          </b-button>
        </div>
        <div v-else class="d-flex justify-content-end align-items-center w-100">
          <b-button
            variant="primary"
            class="px-4 py-2 font-weight-bold shadow-sm"
            :disabled="executionState === 'RUNNING'"
            @click="finishMutasiModal"
          >
            Tutup & Selesai
          </b-button>
        </div>
      </template>
    </b-modal>

  </div>
</template>

<script>
import Breadcrumbs from "@/components/Breadcrumbs";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import VueFeather from "vue-feather";
import MsService from "@/services/MsService";
import SdiOrganizationService from "@/services/SdiOrganizationService";
import moment from "moment";

export default {
  name: "MutasiProdusenKegiatan",
  components: {
    Breadcrumbs,
    Treeselect,
    VueFeather,
  },
  data() {
    return {
      breadcrumbs: [
        { text: "Beranda", href: "/" },
        { text: "Metadata", href: "/metadata-dashboard" },
        { text: "Pengaturan", href: "#" },
        { text: "Mutasi Produsen Kegiatan", href: "/metadata/pengaturan/mutasi-produsen-kegiatan", active: true },
      ],
      searchId: "",
      isLoadingKegiatan: false,
      searchError: null,
      selectedKegiatan: null,
      childVariables: [],
      childIndicators: [],
      showChildDetails: false,

      // Target Form
      targetProdusen: null,
      produsenOptions: null,
      selectedTargetOrg: null,
      targetUnitKerja: "",
      mutationReason: "",
      cascadeOptions: {
        kegiatan: true,
        variabel: true,
        indikator: true,
      },
      defaultProdusenList: [],

      // Execution Modal State
      executionState: "IDLE", // IDLE, RUNNING, DONE
      showPayloadPreview: false,
      executionLogs: [],
      progress: { done: 0, total: 1, failed: 0 },
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters["auth/user"] || {};
    },
    userRoles() {
      const u = this.currentUser;
      if (!u || !u.role) return [];
      if (Array.isArray(u.role)) return u.role;
      if (typeof u.role === "string") {
        const raw = u.role.trim();
        if (raw.startsWith("[") && raw.endsWith("]")) {
          return raw.slice(1, -1).split(",").map((s) => s.trim());
        }
        return raw.split(",").map((s) => s.trim());
      }
      return [];
    },
    currentRoleName() {
      if (this.userRoles.includes("ROLE_SUPERADMIN")) return "Superadmin";
      if (this.userRoles.includes("ROLE_PEMBINA_DATA_PUSAT")) return "Pembina Data Pusat (Dit. Diseminasi)";
      return this.userRoles[0] || "User";
    },
    canAccess() {
      const roles = this.userRoles || [];
      const isSuper =
        roles.includes("ROLE_SUPERADMIN") || roles.includes("ROLE_ADMIN");
      const isPembinaPusat = roles.includes("ROLE_PEMBINA_DATA_PUSAT");
      const isSpecialUser =
        this.currentUser &&
        (this.currentUser.email === "brian.firdian@gmail.com" ||
          this.currentUser.email === "brianf@bps.go.id");
      return isSuper || isPembinaPusat || isSpecialUser;
    },
    accessBadgeText() {
      if (this.userRoles.includes("ROLE_SUPERADMIN")) {
        return "Role: SUPERADMIN";
      }
      return "Role: Pembina Data Pusat (Dit. Diseminasi BPS)";
    },
    isReadyToMutate() {
      return (
        this.selectedKegiatan &&
        this.selectedTargetOrg &&
        this.mutationReason.trim().length > 3
      );
    },
    payloadData() {
      if (!this.selectedKegiatan || !this.selectedTargetOrg) return {};
      return {
        action: "MUTASI_PRODUSEN_DATA",
        kegiatan_id: this.selectedKegiatan.id,
        kegiatan_judul: this.getKegiatanTitle(this.selectedKegiatan),
        source_produsen: {
          id: this.selectedKegiatan.produsen_data_id,
          name: this.selectedKegiatan.produsen_data_name,
          province_code: this.selectedKegiatan.produsen_data_province_code,
          city_code: this.selectedKegiatan.produsen_data_city_code,
        },
        target_produsen: {
          id: this.selectedTargetOrg.id,
          name: this.selectedTargetOrg.name,
          province_code: this.selectedTargetOrg.provinceCode || "00",
          city_code: this.selectedTargetOrg.cityCode || "00",
          unit_kerja: this.targetUnitKerja || null,
        },
        cascade: {
          update_kegiatan: this.cascadeOptions.kegiatan,
          update_variabel: this.cascadeOptions.variabel,
          total_variabel: this.cascadeOptions.variabel ? this.childVariables.length : 0,
          variabel_ids: this.cascadeOptions.variabel ? this.childVariables.map(v => v.id) : [],
          update_indikator: this.cascadeOptions.indikator,
          total_indikator: this.cascadeOptions.indikator ? this.childIndicators.length : 0,
          indikator_ids: this.cascadeOptions.indikator ? this.childIndicators.map(i => i.id) : [],
        },
        reason: this.mutationReason,
        executor: {
          username: this.currentUser.username,
          name: this.currentUser.name,
          role: this.currentRoleName,
          timestamp: new Date().toISOString(),
        },
      };
    },
    formattedPayloadJson() {
      return JSON.stringify(this.payloadData, null, 2);
    },
    executionPlannedTime() {
      const months = [
        'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
        'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
      ];
      const date = new Date();
      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day} ${month} ${year}, ${hours}:${minutes} WIB`;
    },
  },
  mounted() {},
  methods: {
    getKegiatanTitle(keg) {
      if (!keg) return 'Tanpa Judul';
      if (keg.data && keg.data.judul_kegiatan) return keg.data.judul_kegiatan;
      return keg.name || 'Tanpa Judul';
    },
    getKegiatanCara(keg) {
      if (!keg || !keg.data) return '-';
      return keg.data.cara_pengumpulan_data || '-';
    },
    getProdusenAsal(keg) {
      if (!keg) return 'Belum diisi';
      if (keg.produsen_data && keg.produsen_data.name) return keg.produsen_data.name;
      if (keg.produsen_data_name) return keg.produsen_data_name;
      if (keg.data && keg.data.blok_i && keg.data.blok_i.instansi_penyelanggara) return keg.data.blok_i.instansi_penyelanggara;
      if (keg.data && keg.data.instansi_penyelenggara) return keg.data.instansi_penyelenggara;
      return 'Belum diisi';
    },
    async searchKegiatan() {
      if (!this.searchId) return;
      this.isLoadingKegiatan = true;
      this.searchError = null;
      this.selectedKegiatan = null;
      this.childVariables = [];
      this.childIndicators = [];
      this.targetProdusen = null;
      this.selectedTargetOrg = null;

      try {
        const res = await MsService.getMsKegById(this.searchId);
        const result = res && res.data && res.data.result;
        if (result && result.length > 0) {
          const keg = result[0];
          this.selectedKegiatan = keg;
          this.childVariables = (keg && keg.ms_var) ? keg.ms_var : [];
          this.childIndicators = (keg && keg.ms_ind) ? keg.ms_ind : [];
        } else {
          this.searchError = `Kegiatan dengan ID ${this.searchId} tidak ditemukan.`;
        }
      } catch (err) {
        console.error("searchKegiatan error:", err);
        const errMsg = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || "Server error";
        this.searchError = `Gagal memuat kegiatan: ${errMsg}`;
      } finally {
        this.isLoadingKegiatan = false;
      }
    },
    produsenDataNormalizer(node) {
      return {
        ...node,
        id: node.id,
        label: node.name || node.label,
      };
    },
    async produsenDataLoadOptions({ action, searchQuery, callback }) {
      let name = (action === "ASYNC_SEARCH") ? searchQuery : (searchQuery || null);
      let page = 0;
      let roles = ['PRODUSEN_DATA', 'WALIDATA'];
      try {
        const result = await SdiOrganizationService.getAllSdiOrganization(page, name, null, null, roles);
        const options = (result && result.data && result.data.content) ? result.data.content : [];
        callback(null, options);
      } catch (error) {
        console.error("Gagal load SdiOrganization:", error);
        callback(null, []);
      }
    },
    onSelectProdusen(node) {
      if (!node) {
        this.selectedTargetOrg = null;
        return;
      }
      this.selectedTargetOrg = {
        ...node,
        id: node.id,
        name: node.name || node.label,
      };
    },
    onDeselectProdusen() {
      this.selectedTargetOrg = null;
    },
    onInputProdusen(val) {
      if (!val) {
        this.selectedTargetOrg = null;
      } else {
        this.selectedTargetOrg = {
          ...val,
          id: val.id,
          name: val.name || val.label,
        };
      }
    },
    mapStatusToString(status) {
      if (typeof status === "number") {
        const map = {
          0: "DRAFT",
          1: "SUBMITTED",
          2: "UNDER_REVIEW",
          3: "CORRECTION_REQUIRED",
          4: "REVISED",
          5: "REJECTED",
          6: "APPROVED",
          7: "PUBLISHED",
        };
        return map[status] || "DRAFT";
      }
      return status || "DRAFT";
    },
    showConfirmModal() {
      this.executionState = "IDLE";
      this.showPayloadPreview = false;
      this.$bvModal.show("modal-confirm-mutasi");
    },
    finishMutasiModal() {
      if (this.$refs.mutasiModal) {
        this.$refs.mutasiModal.hide();
      } else {
        this.$bvModal.hide('modal-confirm-mutasi');
      }
    },
    getSubmitterPayload() {
      const u = this.currentUser || {};
      const sdiOrg = u.sdiOrganization || {};
      const sdiDetails = sdiOrg.details || {};
      const provCode = (sdiDetails.province && sdiDetails.province.code) || sdiDetails.province_code || u.idProvinsi || "00";
      const provName = (sdiDetails.province && sdiDetails.province.name) || u.provinsi || "PUSAT";
      const cityCode = (sdiDetails.city && sdiDetails.city.code) || sdiDetails.city_code || u.idKabupatenKota || "0000";
      const cityName = (sdiDetails.city && sdiDetails.city.name) || u.kabupatenKota || "PUSAT";

      return {
        id: u.id || 1,
        name: u.name || "Administrator",
        username: u.username || "admin",
        email: u.email || "admin@bps.go.id",
        organization: {
          id: sdiOrg.id || u.idOrganisasi || (this.selectedTargetOrg && this.selectedTargetOrg.id) || 15375,
          name: sdiOrg.name || u.organisasi || (this.selectedTargetOrg && this.selectedTargetOrg.name) || "Badan Pusat Statistik",
          address: sdiDetails.address || "-",
          phone_number: sdiDetails.phone_number || sdiDetails.phone || "-",
          email: sdiDetails.email || u.email || "admin@bps.go.id",
          province_code: provCode,
          city_code: cityCode,
          province: {
            code: provCode,
            name: provName,
          },
          city: {
            code: cityCode,
            name: cityName,
          },
        },
      };
    },
    appendLog(text, type = "info") {
      const now = moment().format("HH:mm:ss");
      this.executionLogs.push({ time: now, text, type });
      this.$nextTick(() => {
        const el = this.$refs.consoleLogContainer;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    },
    getLogClass(type) {
      if (type === "step") return "text-info font-weight-bold";
      if (type === "success") return "text-success";
      if (type === "error") return "text-danger font-weight-bold";
      if (type === "warn") return "text-warning";
      return "text-light";
    },
    async executeMutasi() {
      if (!this.selectedKegiatan || !this.selectedTargetOrg) return;
      this.executionState = "RUNNING";
      this.executionLogs = [];
      const totalSteps = 1 + 
        (this.cascadeOptions.variabel ? this.childVariables.length : 0) + 
        (this.cascadeOptions.indikator ? this.childIndicators.length : 0);
      this.progress = { done: 0, total: totalSteps, failed: 0 };

      const submitterObj = this.getSubmitterPayload();
      const targetOrg = {
        id: parseInt(this.selectedTargetOrg.id, 10),
        name: this.selectedTargetOrg.name || this.selectedTargetOrg.label,
        province_code: this.selectedTargetOrg.provinceCode || (this.selectedTargetOrg.details && this.selectedTargetOrg.details.province && this.selectedTargetOrg.details.province.code) || "00",
        city_code: this.selectedTargetOrg.cityCode || (this.selectedTargetOrg.details && this.selectedTargetOrg.details.city && this.selectedTargetOrg.details.city.code) || "0000",
      };

      const kegId = this.selectedKegiatan.id;
      this.appendLog("=== Memulai Mutasi Produsen Kegiatan ID #" + kegId + " ===", "step");
      this.appendLog("Target Produsen: " + targetOrg.name + " (ID: " + targetOrg.id + ")", "info");

      let wpId = (this.selectedTargetOrg && this.selectedTargetOrg.details && this.selectedTargetOrg.details.walidata && this.selectedTargetOrg.details.walidata.id) ||
                 (this.selectedKegiatan && (this.selectedKegiatan.walidata_pusat_id || (this.selectedKegiatan.walidata_pusat && this.selectedKegiatan.walidata_pusat.id))) || null;
      let wpName = (this.selectedTargetOrg && this.selectedTargetOrg.details && this.selectedTargetOrg.details.walidata && this.selectedTargetOrg.details.walidata.name) ||
                   (this.selectedKegiatan && (this.selectedKegiatan.walidata_pusat_name || (this.selectedKegiatan.walidata_pusat && this.selectedKegiatan.walidata_pusat.name))) || null;
      if (!wpId || wpId === "" || isNaN(parseInt(wpId, 10))) {
        wpId = null;
      } else {
        wpId = parseInt(wpId, 10);
      }
      const walidataPusat = wpId ? {
        id: wpId,
        name: wpName || null,
      } : null;

      try {
        // 1. Update Kegiatan
        this.appendLog("[1/3] Memperbarui Metadata Kegiatan #" + kegId + "...", "step");
        let rawKegData = typeof this.selectedKegiatan.data === "string" ? JSON.parse(this.selectedKegiatan.data) : (this.selectedKegiatan.data || {});
        let kegData = JSON.parse(JSON.stringify(rawKegData));
        if (kegData.blok_i) {
          kegData.blok_i.instansi_penyelanggara = targetOrg.name;
          if (this.selectedTargetOrg.details && kegData.blok_i.alamat_instansi_penyelenggara) {
            const det = this.selectedTargetOrg.details;
            if (det.address) kegData.blok_i.alamat_instansi_penyelenggara.alamat = det.address;
            if (det.phone) kegData.blok_i.alamat_instansi_penyelenggara.telepon = det.phone;
            if (det.email) kegData.blok_i.alamat_instansi_penyelenggara.email = det.email;
          }
        }
        if (this.targetUnitKerja && this.targetUnitKerja.trim()) {
          if (kegData.blok_ii && kegData.blok_ii.unit_penanggung_jawab) {
            kegData.blok_ii.unit_penanggung_jawab.eselon2 = this.targetUnitKerja.trim();
          }
        }

        const kegPayload = {
          base_id: this.selectedKegiatan.base_id || this.selectedKegiatan.id || null,
          submission_period: parseInt(this.selectedKegiatan.submission_period, 10) || new Date().getFullYear(),
          data: kegData,
          reviews: this.selectedKegiatan.reviews ? [...this.selectedKegiatan.reviews] : [],
          status: this.mapStatusToString(this.selectedKegiatan.status),
          rejection_note: this.selectedKegiatan.rejection_note || null,
          submitter: submitterObj,
          produsen_data: targetOrg,
          walidata_pusat: walidataPusat,
          version: "v2",
          changes: null,
        };

        const resKeg = await MsService.updateMsKeg(kegPayload, kegId);
        const resData = resKeg && resKeg.data;
        const resItem = (resData && resData.result && resData.result[0]) || (resData && resData.result) || resData;

        if (resItem && (resItem.status === 403 || resItem.status === 500 || resItem.status === 400 || resItem.error)) {
          const detail = resItem.detail || resItem.title || resItem.message || resItem.error || ("Status HTTP " + resItem.status);
          throw new Error("Server MMS menolak penyimpanan: " + detail);
        }

        this.progress.done += 1;
        this.appendLog("✓ Metadata Kegiatan #" + kegId + " berhasil dimutasi ke " + targetOrg.name, "success");

        // 2. Cascade MS-Variabel
        if (this.cascadeOptions.variabel && this.childVariables.length > 0) {
          this.appendLog("[2/3] Memutasi " + this.childVariables.length + " MS-Variabel...", "step");
          for (let i = 0; i < this.childVariables.length; i++) {
            const v = this.childVariables[i];
            try {
              let vRaw = typeof v.data === "string" ? JSON.parse(v.data) : (v.data || {});
              let vData = JSON.parse(JSON.stringify(vRaw));
              const varPayload = {
                ms_keg_id: kegId,
                submission_period: parseInt(v.submission_period || this.selectedKegiatan.submission_period, 10) || new Date().getFullYear(),
                data: vData,
                reviews: v.reviews ? [...v.reviews] : [],
                status: this.mapStatusToString(v.status),
                rejection_note: v.rejection_note || null,
                submitter: submitterObj,
                produsen_data: targetOrg,
                walidata_pusat: walidataPusat,
                version: "v2",
                changes: null,
              };
              const resVar = await MsService.updateMsVar(varPayload, v.id);
              const rVarItem = (resVar && resVar.data && resVar.data.result && resVar.data.result[0]) || (resVar && resVar.data);
              if (rVarItem && (rVarItem.status === 403 || rVarItem.status === 500 || rVarItem.status === 400 || rVarItem.error)) {
                throw new Error(rVarItem.detail || rVarItem.title || ("Status " + rVarItem.status));
              }

              this.progress.done += 1;
              this.appendLog("  ✓ [" + (i + 1) + "/" + this.childVariables.length + "] MS-Var ID #" + v.id + " (" + (vData.nama || "-") + ") tersimpan", "success");
            } catch (err) {
              console.error("Gagal update MS-Var #" + v.id + ":", err);
              this.progress.failed += 1;
              const errText = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || "Error";
              this.appendLog("  ✗ [" + (i + 1) + "/" + this.childVariables.length + "] MS-Var ID #" + v.id + " gagal: " + errText, "error");
            }
          }
        } else {
          this.appendLog("[2/3] Mutasi MS-Variabel dilewati", "info");
        }

        // 3. Cascade MS-Indikator
        if (this.cascadeOptions.indikator && this.childIndicators.length > 0) {
          this.appendLog("[3/3] Memutasi " + this.childIndicators.length + " MS-Indikator...", "step");
          for (let i = 0; i < this.childIndicators.length; i++) {
            const ind = this.childIndicators[i];
            try {
              let indRaw = typeof ind.data === "string" ? JSON.parse(ind.data) : (ind.data || {});
              let indData = JSON.parse(JSON.stringify(indRaw));
              const indPayload = {
                ms_keg_id: kegId,
                submission_period: parseInt(ind.submission_period || this.selectedKegiatan.submission_period, 10) || new Date().getFullYear(),
                data: indData,
                reviews: ind.reviews ? [...ind.reviews] : [],
                status: this.mapStatusToString(ind.status),
                rejection_note: ind.rejection_note || null,
                submitter: submitterObj,
                produsen_data: targetOrg,
                walidata_pusat: walidataPusat,
                version: "v2",
                changes: null,
              };
              const resInd = await MsService.updateMsInd(indPayload, ind.id);
              const rIndItem = (resInd && resInd.data && resInd.data.result && resInd.data.result[0]) || (resInd && resInd.data);
              if (rIndItem && (rIndItem.status === 403 || rIndItem.status === 500 || rIndItem.status === 400 || rIndItem.error)) {
                throw new Error(rIndItem.detail || rIndItem.title || ("Status " + rIndItem.status));
              }

              this.progress.done += 1;
              this.appendLog("  ✓ [" + (i + 1) + "/" + this.childIndicators.length + "] MS-Ind ID #" + ind.id + " (" + (indData.nama || "-") + ") tersimpan", "success");
            } catch (err) {
              console.error("Gagal update MS-Ind #" + ind.id + ":", err);
              this.progress.failed += 1;
              const errText = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || "Error";
              this.appendLog("  ✗ [" + (i + 1) + "/" + this.childIndicators.length + "] MS-Ind ID #" + ind.id + " gagal: " + errText, "error");
            }
          }
        } else {
          this.appendLog("[3/3] Mutasi MS-Indikator dilewati", "info");
        }

        if (this.progress.failed === 0) {
          this.appendLog("=== MUTASI SELESAI: Seluruh " + this.progress.done + " item berhasil dimutasi ===", "success");
          this.executionState = "DONE";
          this.$bvToast.toast("Mutasi produsen kegiatan #" + kegId + " beserta seluruh item terkait berhasil diproses.", {
            title: "Mutasi Berhasil",
            variant: "success",
            solid: true,
            autoHideDelay: 6000,
          });
        } else {
          this.appendLog("=== MUTASI SELESAI DENGAN CATATAN: " + this.progress.done + " berhasil, " + this.progress.failed + " gagal ===", "warn");
          this.executionState = "DONE";
          this.$bvToast.toast("Mutasi selesai dengan catatan: " + this.progress.failed + " item gagal diproses. Silakan periksa log rincian.", {
            title: "Mutasi Belum Sempurna",
            variant: "warning",
            solid: true,
            autoHideDelay: 6000,
          });
        }

        // Re-fetch current kegiatan so the UI immediately updates
        await this.searchKegiatan();
      } catch (err) {
        console.error("executeMutasi error:", err);
        this.progress.failed += 1;
        const errMsg = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || "Terjadi kesalahan sistem";
        this.appendLog("✗ Eksekusi mutasi gagal: " + errMsg, "error");
        this.executionState = "DONE";
        this.$bvToast.toast("Mutasi gagal: " + errMsg, {
          title: "Eksekusi Gagal",
          variant: "danger",
          solid: true,
          autoHideDelay: 7000,
        });
      }
    },
    copyPayloadJson() {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.formattedPayloadJson);
        this.$bvToast.toast("Payload JSON berhasil disalin ke clipboard.", {
          title: "Tersalin",
          variant: "info",
          solid: true,
          autoHideDelay: 2000,
        });
      }
    },
    resetForm() {
      this.selectedKegiatan = null;
      this.targetProdusen = null;
      this.selectedTargetOrg = null;
      this.targetUnitKerja = "";
      this.mutationReason = "";
      this.childVariables = [];
      this.childIndicators = [];
      this.searchId = "";
    },
  },
};
</script>

<style scoped lang="scss">
.status {
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  &--published {
    background-color: #e6fffa;
    color: #047481;
    border: 1px solid #79f2d0;
  }
  &--approved {
    background-color: #ebfbee;
    color: #2b8a3e;
    border: 1px solid #b2f2bb;
  }
  &--draft {
    background-color: #f1f3f5;
    color: #495057;
    border: 1px solid #ced4da;
  }
  &--rejected {
    background-color: #fff5f5;
    color: #c92a2a;
    border: 1px solid #ffc9c9;
  }
}

.summary-transfer-card {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.transfer-arrow-pill {
  transition: all 0.2s ease;
  &:hover {
    transform: scale(1.08);
  }
}
.bg-primary-light {
  background-color: #eff6ff;
}
.bg-info-light {
  background-color: #f0f9ff;
}
.bg-success-light {
  background-color: #f0fdf4;
}
.bg-warning-light {
  background-color: #fffbeb;
}
.darkroom-console {
  border-radius: 8px;
  overflow: hidden;
}
</style>
